import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const SC_BRUEHL_LOGO = 'https://scbruehl.ch/wp-content/themes/scbruehl/assets/img/scb_logo.svg';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await axios.post(`${API_URL}/auth/forgot-password`, { email });
            setSent(true);
        } catch (error) {
            toast.error('Fehler beim Senden. Bitte versuche es erneut.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-background min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-md animate-scale-in">
                <div className="text-center mb-8">
                    <img src={SC_BRUEHL_LOGO} alt="SC Brühl St. Gallen" className="w-20 h-20 mx-auto mb-4" />
                    <h1 className="text-4xl font-heading text-white tracking-tight">Kronentipp</h1>
                    <p className="text-green-200 mt-2 font-body">SC Brühl St. Gallen - Tippspiel</p>
                </div>

                <Card className="glass-card border-white/10">
                    {sent ? (
                        <CardContent className="pt-8 pb-8 text-center space-y-5">
                            <div className="flex justify-center">
                                <div className="w-16 h-16 rounded-full bg-[#00ad43]/20 flex items-center justify-center">
                                    <CheckCircle className="w-9 h-9 text-[#00ad43]" />
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold" data-testid="reset-sent-title">Email gesendet</h2>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                Falls ein Konto mit <strong>{email}</strong> existiert, haben wir einen Link zum Zurücksetzen gesendet. Prüfe dein Postfach.
                            </p>
                            <p className="text-muted-foreground text-xs">Der Link ist 1 Stunde gültig.</p>
                            <Link to="/login">
                                <Button variant="outline" className="gap-2 mt-2" data-testid="back-to-login-button">
                                    <ArrowLeft className="w-4 h-4" />
                                    Zurück zur Anmeldung
                                </Button>
                            </Link>
                        </CardContent>
                    ) : (
                        <>
                            <CardHeader className="space-y-1">
                                <CardTitle className="text-2xl text-center">Passwort vergessen</CardTitle>
                                <p className="text-center text-sm text-muted-foreground">
                                    Gib deine Email-Adresse ein und wir senden dir einen Link zum Zurücksetzen.
                                </p>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email *</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="ihre@email.ch"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="pl-10"
                                                required
                                                data-testid="forgot-email-input"
                                            />
                                        </div>
                                    </div>
                                    <Button 
                                        type="submit" 
                                        className="w-full btn-primary"
                                        disabled={isLoading}
                                        data-testid="forgot-submit-button"
                                    >
                                        {isLoading ? 'Wird gesendet...' : 'Link senden'}
                                    </Button>
                                </form>
                                <div className="mt-6 text-center text-sm">
                                    <Link to="/login" className="text-primary hover:underline font-medium flex items-center justify-center gap-1" data-testid="back-login-link">
                                        <ArrowLeft className="w-3 h-3" />
                                        Zurück zur Anmeldung
                                    </Link>
                                </div>
                            </CardContent>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}
